/**
 * FloorPlanDocument — wersjonowany, ustrukturyzowany model rzutu (single source of truth).
 *
 * Współrzędne ZNORMALIZOWANE: x,y ∈ [0,1] względem obrazu źródłowego, origin = lewy-górny róg.
 * Zgodne z konwencją istniejącego `floorplan-detect.validator.ts`. Realne milimetry TYLKO gdy
 * skala jest `confirmed` — nie wyprowadzamy wymiarów z pikseli bez potwierdzonej skali.
 *
 * Rozdział odpowiedzialności:
 *   • Zod (ten plik)          — kontrakt STRUKTURALNY: kształt, enumy, liczby skończone (nie NaN/Inf).
 *   • GeometryValidator       — kontrakt SEMANTYCZNY: zakresy 0..1, degeneracje, referencje, unikalność.
 * Dzięki temu geometria out-of-range może być RAPORTOWANA i auto-naprawiona zamiast twardo odrzucona.
 */
import { z } from "zod";

export const SCHEMA_VERSION = "1.0" as const;

/** Liczba skończona (odrzuca NaN oraz ±Infinity). Baza dla wszystkich współrzędnych/rozmiarów. */
const Finite = z.number().finite();

// ── Wspólne enumy ─────────────────────────────────────────────────────────────
export const ObjectSourceSchema = z.enum(["ai", "user", "editor"]);
export const ReviewStatusSchema = z.enum(["unreviewed", "confirmed", "corrected"]);

export const WallTypeSchema = z.enum(["exterior", "interior", "unknown"]);
export const OpeningTypeSchema = z.enum(["door", "window", "balcony_door", "opening", "unknown"]);
export const OpeningDirectionSchema = z.enum([
  "left", "right", "inward", "outward", "sliding", "unknown",
]);
export const RoomTypeSchema = z.enum([
  "living_room", "bedroom", "kitchen", "bathroom", "hallway", "office",
  "dining_room", "storage", "wardrobe", "utility_room", "balcony", "terrace",
  "other", "unknown",
]);
export const AreaStatusSchema = z.enum(["unknown", "estimated", "confirmed"]);
export const FixtureTypeSchema = z.enum([
  "toilet", "sink", "bathtub", "shower", "kitchen_sink", "stove",
  "refrigerator", "dishwasher", "washing_machine", "radiator", "other", "unknown",
]);
export const FurnitureTypeSchema = z.enum([
  "bed", "sofa", "table", "chair", "wardrobe", "cabinet", "desk", "tv", "other", "unknown",
]);
export const DimensionSourceSchema = z.enum(["detected_text", "user"]);
export const UncertaintyTypeSchema = z.enum([
  "missing_wall", "uncertain_wall", "uncertain_window", "uncertain_door",
  "uncertain_room", "conflicting_dimension", "unreadable_area", "scale_unknown", "other",
]);
export const SeveritySchema = z.enum(["info", "warning", "blocking"]);

export const DocumentStatusSchema = z.enum(["draft", "needs_review", "confirmed", "completed"]);
export const AnalysisStatusSchema = z.enum([
  "not_started", "processing", "needs_review", "completed", "failed",
]);
export const RealWorldScaleStatusSchema = z.enum(["unknown", "estimated", "confirmed"]);
export const SourceTypeSchema = z.enum(["upload", "editor", "ai_import"]);

// ── Prymitywy geometryczne ────────────────────────────────────────────────────
/** Punkt w układzie znormalizowanym. Zakres [0,1] sprawdza GeometryValidator (nie Zod). */
export const PointSchema = z.object({ x: Finite, y: Finite });

/** Konfidencja: 0..1 lub null (auto-fix może ustawić null dla wartości poza zakresem). */
const Confidence = Finite.nullable();

const Uuid = z.string().min(1);

// ── Obiekty geometryczne ──────────────────────────────────────────────────────
export const WallSchema = z.object({
  id: Uuid,
  type: WallTypeSchema,
  start: PointSchema,
  end: PointSchema,
  thickness: Finite,
  heightMm: Finite.nullable().default(null),
  confidence: Confidence,
  source: ObjectSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

export const OpeningSchema = z.object({
  id: Uuid,
  type: OpeningTypeSchema,
  wallId: Uuid.nullable().default(null),
  position: PointSchema,
  width: Finite,
  depth: Finite.nullable().default(null),
  rotationDegrees: Finite.default(0),
  openingDirection: OpeningDirectionSchema.default("unknown"),
  confidence: Confidence,
  source: ObjectSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

export const RoomSchema = z.object({
  id: Uuid,
  type: RoomTypeSchema,
  name: z.string().default(""),
  polygon: z.array(PointSchema).default([]),
  areaM2: Finite.nullable().default(null),
  areaStatus: AreaStatusSchema.default("unknown"),
  confidence: Confidence,
  source: ObjectSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

const SizeSchema = z.object({ width: Finite, height: Finite });

export const FixtureSchema = z.object({
  id: Uuid,
  type: FixtureTypeSchema,
  position: PointSchema,
  size: SizeSchema,
  rotationDegrees: Finite.default(0),
  confidence: Confidence,
  source: ObjectSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

export const FurnitureSchema = z.object({
  id: Uuid,
  type: FurnitureTypeSchema,
  position: PointSchema,
  size: SizeSchema,
  rotationDegrees: Finite.default(0),
  confidence: Confidence,
  source: ObjectSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

export const LabelSchema = z.object({
  id: Uuid,
  text: z.string(),
  position: PointSchema,
  rotationDegrees: Finite.default(0),
  confidence: Confidence,
  source: ObjectSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

export const DimensionSchema = z.object({
  id: Uuid,
  value: Finite,
  unit: z.literal("mm").default("mm"),
  start: PointSchema,
  end: PointSchema,
  confidence: Confidence,
  source: DimensionSourceSchema,
  reviewStatus: ReviewStatusSchema,
});

export const IgnoredObjectSchema = z.object({
  id: Uuid,
  reason: z.string().default(""),
  region: z.object({ x: Finite, y: Finite, width: Finite, height: Finite }).nullable().default(null),
});

export const UncertaintySchema = z.object({
  id: Uuid,
  type: UncertaintyTypeSchema,
  message: z.string(),
  objectIds: z.array(Uuid).default([]),
  region: z
    .object({ x: Finite, y: Finite, width: Finite, height: Finite })
    .nullable()
    .default(null),
  severity: SeveritySchema,
  confidence: Confidence,
  resolved: z.boolean().default(false),
  resolution: z.string().nullable().default(null),
});

// ── Sekcje dokumentu ──────────────────────────────────────────────────────────
export const SourceSchema = z.object({
  type: SourceTypeSchema,
  fileId: Uuid.nullable().default(null),
  mimeType: z.string().nullable().default(null),
  widthPx: Finite.nullable().default(null),
  heightPx: Finite.nullable().default(null),
  originalFileName: z.string().nullable().default(null),
  page: Finite.default(1),
  rotationDegrees: Finite.default(0),
});

export const CoordinateSystemSchema = z.object({
  type: z.literal("normalized").default("normalized"),
  origin: z.literal("top_left").default("top_left"),
  xRange: z.tuple([Finite, Finite]).default([0, 1]),
  yRange: z.tuple([Finite, Finite]).default([0, 1]),
  units: z.literal("normalized").default("normalized"),
  realWorldScale: z
    .object({
      status: RealWorldScaleStatusSchema.default("unknown"),
      millimetersPerNormalizedUnit: Finite.nullable().default(null),
      referenceSource: z.string().nullable().default(null),
    })
    .default({ status: "unknown", millimetersPerNormalizedUnit: null, referenceSource: null }),
});

export const BuildingSchema = z.object({
  outline: z.array(PointSchema).default([]),
  floorNumber: Finite.nullable().default(null),
  northAngleDegrees: Finite.nullable().default(null),
});

export const AnalysisSchema = z.object({
  status: AnalysisStatusSchema.default("not_started"),
  documentType: z.string().nullable().default(null),
  overallConfidence: Confidence.default(null),
  model: z.string().nullable().default(null),
  promptVersion: z.string().nullable().default(null),
  startedAt: z.string().nullable().default(null),
  completedAt: z.string().nullable().default(null),
  warnings: z.array(z.string()).default([]),
});

export const MetadataSchema = z.object({
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().nullable().default(null),
  lastModifiedBy: z.string().nullable().default(null),
});

// ── Dokument ──────────────────────────────────────────────────────────────────
export const FloorPlanDocumentSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  projectId: z.string(),
  revision: z.number().int().nonnegative(),
  status: DocumentStatusSchema,
  source: SourceSchema,
  coordinateSystem: CoordinateSystemSchema,
  building: BuildingSchema,
  walls: z.array(WallSchema).default([]),
  openings: z.array(OpeningSchema).default([]),
  rooms: z.array(RoomSchema).default([]),
  fixtures: z.array(FixtureSchema).default([]),
  furniture: z.array(FurnitureSchema).default([]),
  labels: z.array(LabelSchema).default([]),
  dimensions: z.array(DimensionSchema).default([]),
  ignoredObjects: z.array(IgnoredObjectSchema).default([]),
  uncertainties: z.array(UncertaintySchema).default([]),
  analysis: AnalysisSchema,
  metadata: MetadataSchema,
});

/** Bezpieczny parse — zwraca `{ success, data | error }` (Zod). */
export function parseFloorPlanDocument(input: unknown) {
  return FloorPlanDocumentSchema.safeParse(input);
}

/**
 * Analiza AI zwraca tylko geometrię (bez metadata/projectId) — ten podschemat waliduje surowy
 * wynik modelu przed złożeniem go w pełny FloorPlanDocument.
 */
export const FloorPlanAnalysisResultSchema = z.object({
  building: BuildingSchema.default({ outline: [], floorNumber: null, northAngleDegrees: null }),
  walls: z.array(WallSchema).default([]),
  openings: z.array(OpeningSchema).default([]),
  rooms: z.array(RoomSchema).default([]),
  fixtures: z.array(FixtureSchema).default([]),
  furniture: z.array(FurnitureSchema).default([]),
  labels: z.array(LabelSchema).default([]),
  dimensions: z.array(DimensionSchema).default([]),
  ignoredObjects: z.array(IgnoredObjectSchema).default([]),
  uncertainties: z.array(UncertaintySchema).default([]),
});
